// The shared half of the three instance batches (the plan's A.4).
//
// A quad, a segment and a circle batch each own one instance buffer, one
// pipeline and one fade, and each flush is the same four steps: skip an
// empty batch, bind the pipeline, write the uniforms and draw the strip.
// The steps are written here once. A batch adds its writers, which fill
// the instance floats, and the uniforms its own program reads.

import type { AttribSpec, BatchDeps, InstanceBuffer, Pipeline } from './types';
import { STRIP_VERTICES } from './instance-array';
import type { CameraUniforms } from '../layer';
import type { UniformSpec } from '../shaders/prelude';

/**
 * The uniform block of the two stroke batches, the segment and the circle,
 * in order: the camera, the zoom, the px per world px and the batch fade.
 *
 * WebGL2 looks every name up and needs no list. WebGPU packs the block into
 * one buffer at byte offsets, so the ORDER is the contract and it is stated
 * here once. The quad batch reads the atlas and has a block of its own.
 */
export const STROKE_UNIFORMS: readonly UniformSpec[] = [
  { name: 'uViewProj', type: 'mat3', value: [1, 0, 0, 0, 1, 0, 0, 0, 1], dynamic: true },
  { name: 'uZoom', type: 'float', value: 1, dynamic: true },
  { name: 'uPxPerWorld', type: 'float', value: 1, dynamic: true },
  { name: 'uAlpha', type: 'float', value: 1, dynamic: true },
];

export abstract class InstanceBatch<D extends BatchDeps = BatchDeps> {
  protected readonly buf: InstanceBuffer;

  /** A fade over everything the batch draws, written as `uAlpha` on flush. */
  globalAlpha = 1;

  constructor(
    protected readonly deps: D,
    /** The program's name, which the `?glcheck` report cites after a draw. */
    private readonly name: string,
    floatsPerInstance: number,
    attribs: readonly AttribSpec[],
    capacity: number,
  ) {
    this.buf = deps.instances(floatsPerInstance, attribs, capacity);
  }

  begin(): void { this.buf.begin(); }
  get count(): number { return this.buf.count; }

  /** Uploads and draws. Returns the instance count, or 0 for an empty batch. */
  flush(cam: CameraUniforms): number {
    if (this.buf.count === 0) return 0;
    const p = this.deps.pipeline;
    if (!p.use()) return 0;
    this.setUniforms(p, cam);
    const n = this.buf.draw(STRIP_VERTICES);
    this.deps.gfx.check(this.name);
    return n;
  }

  /** Writes the uniforms of one flush, with the pipeline in use. */
  protected abstract setUniforms(p: Pipeline, cam: CameraUniforms): void;

  dispose(): void { this.buf.dispose(); }
}

/**
 * The base of the two stroke batches, the segment and the circle. Both
 * programs read the block of `STROKE_UNIFORMS`, so one flush writes it for
 * both.
 */
export abstract class StrokeBatch extends InstanceBatch {
  protected setUniforms(p: Pipeline, cam: CameraUniforms): void {
    p.uniformMatrix3fv('uViewProj', cam.viewProj);
    p.uniform1f('uZoom', cam.zoom);
    p.uniform1f('uPxPerWorld', cam.pxPerWorld);
    p.uniform1f('uAlpha', this.globalAlpha);
  }
}
